import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Film } from "@/mocks/films";

type HeroCarouselProps = {
  slides: Film[];
};

const HeroCarousel = ({ slides }: HeroCarouselProps) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <section className="relative flex h-[560px] w-full items-center justify-center bg-foreground-950 px-4 text-center">
        <div>
          <p className="font-heading text-2xl text-background-50">焦点内容整理中</p>
          <p className="mt-2 text-sm text-background-300">
            暂无焦点推荐，你可以先去分类页浏览全部影视资料。
          </p>
          <Link
            to="/category"
            className="mt-5 inline-flex rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-foreground-950"
          >
            浏览影视分类
          </Link>
        </div>
      </section>
    );
  }

  const current = slides[active];

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-[900ms] ${
            index === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.still}
            alt={`${slide.title} 剧照`}
            title={`${slide.title} ${slide.year} 焦点推荐`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/70 to-foreground-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-transparent to-foreground-950/45" />

      <div className="relative mx-auto flex min-h-[600px] w-full max-w-[1280px] flex-col justify-end px-4 pb-10 pt-28 md:min-h-[680px] md:px-8 md:pb-16">
        <div className="w-full max-w-2xl">
          <div key={current.id} className="animate-fade-up">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-foreground-950">
                焦点推荐
              </span>
              <span className="text-xs tracking-[0.2em] text-background-200/80">
                {current.originalTitle}
              </span>
            </div>
            <h2 className="font-heading text-[34px] font-bold leading-[1.15] text-background-50 md:text-[52px]">
              {current.title}
            </h2>
            <p className="mt-4 max-w-xl font-heading text-base leading-relaxed text-background-100/90 md:text-lg">
              {current.tagline}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200/80">
              <span className="flex items-center gap-1.5 font-semibold text-accent-400">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-star-fill text-xs" />
                </span>
                {current.rating.toFixed(1)}
              </span>
              <span>{current.year}</span>
              <span className="hidden text-background-500 sm:inline">|</span>
              <span className="hidden sm:inline">{current.region}</span>
              <span className="hidden text-background-500 sm:inline">|</span>
              <span className="hidden sm:inline">{current.genres.join(" · ")}</span>
              <span className="hidden text-background-500 sm:inline">|</span>
              <span className="hidden sm:inline">{current.duration} 分钟</span>
            </div>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200/75 line-clamp-2 md:line-clamp-3">
              {current.synopsis}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-400"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-book-open-line" />
                </span>
                查看剧情详情
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 rounded-md border border-background-100/35 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:border-background-50 hover:bg-background-50/10"
              >
                全部影视作品
              </Link>
            </div>
          </div>

          <div className="mt-9 flex items-center gap-3">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => setActive(index)}
                aria-label={`切换到 ${slide.title}`}
                className="group flex items-center gap-2 cursor-pointer"
              >
                <span
                  className={`h-[3px] rounded-full transition-all duration-300 ${
                    index === active ? "w-10 bg-accent-500" : "w-5 bg-background-100/35 group-hover:bg-background-100/70"
                  }`}
                />
              </button>
            ))}
            <span className="ml-2 font-label text-xs tracking-widest text-background-200/60">
              {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;